import {
  MessarioAiContextMessage,
  MessarioAiDraftResponse,
  MessarioAiGenerateBundle,
  MessarioAiGenerateDraftPayload,
  MessarioAiVariableDefinition,
  MessarioAiRuntime,
  MessarioAiTrainingCaseResponse,
  MessarioAiTrainingChange
} from '@lib/messarioAi/types';

export class MessarioAiFetchFailure extends Error {
  constructor(
    public readonly kind: 'configuration' | 'network' | 'rejected' | 'invalid-response' | 'timeout',
    message: string,
    public readonly status?: number,
    public readonly errorCode?: string,
    public readonly source?: 'openai' | 'panel'
  ) {
    super(message);
    this.name = 'MessarioAiFetchFailure';
  }
}

function panelOriginNormalized(panelOrigin: string) {
  return panelOrigin.replace(/\/+$/, '');
}

async function readJsonBody(response: Response): Promise<Record<string, unknown>> {
  try {
    return await response.json() as Record<string, unknown>;
  } catch(err) {
    throw new MessarioAiFetchFailure(
      'invalid-response',
      'Messario Panel returned an unreadable AI response.',
      response.status
    );
  }
}

function assertDraftResponse(body: Record<string, unknown>, status: number): MessarioAiDraftResponse {
  if(
    typeof body.reply !== 'string' ||
    typeof body.draft_generate_id !== 'number' ||
    (body.training_event_id !== null && typeof body.training_event_id !== 'number') ||
    typeof body.prompt_revision !== 'number'
  ) {
    throw new MessarioAiFetchFailure(
      'invalid-response',
      'Messario Panel returned an incomplete AI draft response.',
      status
    );
  }

  return {
    reply: body.reply,
    draft_generate_id: body.draft_generate_id,
    training_event_id: body.training_event_id as number | null,
    prompt_revision: body.prompt_revision,
    usage: (body.usage && typeof body.usage === 'object' ?
      body.usage :
      {
        model: '',
        external_input_tokens: 0,
        external_output_tokens: 0,
        ai_tokens: 0,
        balance_after: 0
      }) as MessarioAiDraftResponse['usage']
  };
}

function requirePanelAuth(token: string, panelOrigin: string) {
  if(!panelOrigin) {
    throw new MessarioAiFetchFailure(
      'configuration',
      'Messario Panel origin is not configured.',
      undefined,
      undefined,
      'panel'
    );
  }

  if(!token) {
    throw new MessarioAiFetchFailure(
      'configuration',
      'Messario chat access token is missing.',
      undefined,
      undefined,
      'panel'
    );
  }
}

async function panelFetchJson(
  token: string,
  panelOrigin: string,
  path: string,
  init: RequestInit,
  fetchValue: typeof fetch
): Promise<{response: Response, body: Record<string, unknown>}> {
  requirePanelAuth(token, panelOrigin);

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    ...(init.headers as Record<string, string> | undefined)
  };
  if(init.body != null) {
    headers['Content-Type'] = 'application/json';
  }

  let response: Response;
  try {
    response = await fetchValue(
      `${panelOriginNormalized(panelOrigin)}${path}`,
      {
        ...init,
        headers
      }
    );
  } catch{
    throw new MessarioAiFetchFailure(
      'network',
      'Could not reach Messario Panel for AI draft.',
      undefined,
      undefined,
      'panel'
    );
  }

  const body = await readJsonBody(response);
  return {response, body};
}

function rejectedPanelFailure(body: Record<string, unknown>, status: number, fallback: string) {
  const errorCode = typeof body.error === 'string' ? body.error : undefined;
  return new MessarioAiFetchFailure(
    'rejected',
    (typeof body.message === 'string' && body.message) ||
      errorCode ||
      fallback,
    status,
    errorCode,
    'panel'
  );
}

export async function fetchMessarioAiGenerateBundle(
  token: string,
  panelOrigin: string,
  fetchValue: typeof fetch = fetch
): Promise<MessarioAiGenerateBundle> {
  const {response, body} = await panelFetchJson(
    token,
    panelOrigin,
    '/api/telegram-web/ai/bundle',
    {method: 'GET'},
    fetchValue
  );

  if(!response.ok) {
    throw rejectedPanelFailure(body, response.status, 'Messario rejected the AI bundle request.');
  }

  if(body.enabled === false) {
    return {enabled: false};
  }

  if(
    body.enabled !== true ||
    typeof body.model !== 'string' ||
    typeof body.reasoning_effort !== 'string' ||
    typeof body.history_limit !== 'number' ||
    typeof body.max_output_tokens !== 'number' ||
    typeof body.timezone !== 'string' ||
    typeof body.channel !== 'string' ||
    typeof body.json_schema_name !== 'string' ||
    !body.json_schema_name ||
    !Array.isArray(body.definitions) ||
    typeof body.prompt_revision !== 'number' ||
    typeof body.variable_definitions_revision !== 'number' ||
    !body.features ||
    typeof body.features !== 'object'
  ) {
    throw new MessarioAiFetchFailure(
      'invalid-response',
      'Messario Panel returned an incomplete AI generate bundle.',
      response.status,
      undefined,
      'panel'
    );
  }

  const features = body.features as {one_shot_instruction?: unknown, draft_only?: unknown};
  return {
    enabled: true,
    model: body.model,
    reasoning_effort: body.reasoning_effort,
    history_limit: body.history_limit,
    max_output_tokens: body.max_output_tokens,
    timezone: body.timezone,
    channel: body.channel,
    json_schema_name: body.json_schema_name,
    definitions: body.definitions as MessarioAiVariableDefinition[],
    prompt_revision: body.prompt_revision,
    variable_definitions_revision: body.variable_definitions_revision,
    features: {
      one_shot_instruction: features.one_shot_instruction === true,
      draft_only: features.draft_only === true
    },
    folder_id: body.folder_id === null || typeof body.folder_id === 'number' ? body.folder_id as number | null : null,
    compiled_at: body.compiled_at === null || typeof body.compiled_at === 'string' ? body.compiled_at as string | null : null
  };
}

export async function fetchMessarioAiRuntime(
  token: string,
  panelOrigin: string,
  peerId: string,
  fetchValue: typeof fetch = fetch
): Promise<MessarioAiRuntime> {
  const {response, body} = await panelFetchJson(
    token,
    panelOrigin,
    `/api/telegram-web/ai/runtime?peer_id=${encodeURIComponent(peerId)}`,
    {method: 'GET'},
    fetchValue
  );

  if(!response.ok) {
    throw rejectedPanelFailure(body, response.status, 'Messario rejected the AI runtime request.');
  }

  const system = body.system && typeof body.system === 'object' ?
    body.system as Record<string, unknown> :
    null;
  if(
    typeof body.peer_id !== 'string' ||
    !system ||
    typeof system.current_date !== 'string' ||
    typeof system.current_datetime !== 'string' ||
    typeof system.timezone !== 'string' ||
    typeof system.channel !== 'string' ||
    typeof body.account_name !== 'string'
  ) {
    throw new MessarioAiFetchFailure(
      'invalid-response',
      'Messario Panel returned an incomplete AI runtime response.',
      response.status,
      undefined,
      'panel'
    );
  }

  const effective = body.effective_values && typeof body.effective_values === 'object' && !Array.isArray(body.effective_values) ?
    body.effective_values as Record<string, unknown> :
    {};

  return {
    peer_id: body.peer_id,
    effective_values: effective,
    system: {
      current_date: system.current_date,
      current_datetime: system.current_datetime,
      timezone: system.timezone,
      channel: system.channel
    },
    account_name: body.account_name
  };
}

export async function requestMessarioAiGenerate(
  token: string,
  panelOrigin: string,
  payload: MessarioAiGenerateDraftPayload,
  fetchValue: typeof fetch = fetch
): Promise<MessarioAiDraftResponse> {
  const bodyPayload: MessarioAiGenerateDraftPayload = {
    peer_id: payload.peer_id,
    messages: payload.messages,
    prompt_revision: payload.prompt_revision,
    variable_definitions_revision: payload.variable_definitions_revision,
    model: payload.model,
    client_attempt_id: payload.client_attempt_id
  };
  if(payload.one_shot_instruction) {
    bodyPayload.one_shot_instruction = payload.one_shot_instruction;
  }

  const {response, body} = await panelFetchJson(
    token,
    panelOrigin,
    '/api/telegram-web/ai/generate',
    {method: 'POST', body: JSON.stringify(bodyPayload)},
    fetchValue
  );

  if(!response.ok) {
    throw rejectedPanelFailure(body, response.status, 'Messario rejected the AI generate request.');
  }

  return assertDraftResponse(body, response.status);
}

export async function requestMessarioAiDraft(
  token: string,
  panelOrigin: string,
  payload: {
    peer_id: string,
    messages: MessarioAiContextMessage[],
    one_shot_instruction?: string
  },
  fetchValue: typeof fetch = fetch
): Promise<MessarioAiDraftResponse> {
  if(!panelOrigin) {
    throw new MessarioAiFetchFailure(
      'configuration',
      'Messario Panel origin is not configured.'
    );
  }

  if(!token) {
    throw new MessarioAiFetchFailure(
      'configuration',
      'Messario chat access token is missing.'
    );
  }

  let response: Response;
  try {
    response = await fetchValue(
      `${panelOriginNormalized(panelOrigin)}/api/telegram-web/ai/draft`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      }
    );
  } catch(err) {
    throw new MessarioAiFetchFailure(
      'network',
      'Could not reach Messario Panel for AI draft.'
    );
  }

  const body = await readJsonBody(response);
  if(!response.ok) {
    throw new MessarioAiFetchFailure(
      'rejected',
      (typeof body.message === 'string' && body.message) ||
        (typeof body.error === 'string' && body.error) ||
        'Messario rejected the AI draft request.',
      response.status
    );
  }

  return assertDraftResponse(body, response.status);
}

export async function completeMessarioAiTrainingEvent(
  token: string,
  panelOrigin: string,
  trainingEventId: number,
  payload: {final_reply_text: string, sent_message_id?: string},
  fetchValue: typeof fetch = fetch
): Promise<void> {
  if(!panelOrigin) {
    throw new MessarioAiFetchFailure(
      'configuration',
      'Messario Panel origin is not configured.'
    );
  }

  if(!token) {
    throw new MessarioAiFetchFailure(
      'configuration',
      'Messario chat access token is missing.'
    );
  }

  if(!trainingEventId) {
    throw new MessarioAiFetchFailure(
      'configuration',
      'Messario AI training event id is missing.'
    );
  }

  let response: Response;
  try {
    response = await fetchValue(
      `${panelOriginNormalized(panelOrigin)}/api/telegram-web/ai/training-events/${trainingEventId}/complete`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      }
    );
  } catch(err) {
    throw new MessarioAiFetchFailure(
      'network',
      'Could not reach Messario Panel for AI training complete.'
    );
  }

  if(!response.ok) {
    throw new MessarioAiFetchFailure(
      'rejected',
      'Messario rejected the AI training complete request.',
      response.status
    );
  }
}

export async function dismissMessarioAiTrainingEvent(
  token: string,
  panelOrigin: string,
  trainingEventId: number,
  fetchValue: typeof fetch = fetch
): Promise<void> {
  if(!panelOrigin || !token || !trainingEventId) {
    return;
  }

  let response: Response;
  try {
    response = await fetchValue(
      `${panelOriginNormalized(panelOrigin)}/api/telegram-web/ai/training-events/${trainingEventId}/dismiss`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: '{}'
      }
    );
  } catch(err) {
    console.warn('Messario AI training dismiss failed', err);
    return;
  }

  if(!response.ok) {
    console.warn('Messario AI training dismiss rejected', response.status);
  }
}

function assertTrainingCaseResponse(
  body: Record<string, unknown>,
  status: number
): MessarioAiTrainingCaseResponse {
  const casePayload = body.case;
  if(!casePayload || typeof casePayload !== 'object') {
    throw new MessarioAiFetchFailure(
      'invalid-response',
      'Messario Panel returned an incomplete training case response.',
      status
    );
  }

  const c = casePayload as Record<string, unknown>;
  if(typeof c.id !== 'number' || typeof c.status !== 'string' || typeof c.draft_generate_id !== 'number') {
    throw new MessarioAiFetchFailure(
      'invalid-response',
      'Messario Panel returned an incomplete training case payload.',
      status
    );
  }

  return {
    case: c as MessarioAiTrainingCaseResponse['case'],
    analyzer_result: (body.analyzer_result && typeof body.analyzer_result === 'object' ?
      body.analyzer_result as Record<string, unknown> :
      (c.analyzer_result as Record<string, unknown> | null | undefined) ?? null),
    proposed_changes: (Array.isArray(body.proposed_changes) ?
      body.proposed_changes as MessarioAiTrainingChange[] :
      (Array.isArray(c.proposed_changes) ? c.proposed_changes as MessarioAiTrainingChange[] : null)),
    conflict: body.conflict === true
  };
}

async function postMessarioAiTrainingCase(
  token: string,
  panelOrigin: string,
  path: string,
  payload: Record<string, unknown>,
  fetchValue: typeof fetch
): Promise<MessarioAiTrainingCaseResponse> {
  if(!panelOrigin) {
    throw new MessarioAiFetchFailure(
      'configuration',
      'Messario Panel origin is not configured.'
    );
  }

  if(!token) {
    throw new MessarioAiFetchFailure(
      'configuration',
      'Messario chat access token is missing.'
    );
  }

  let response: Response;
  try {
    response = await fetchValue(
      `${panelOriginNormalized(panelOrigin)}${path}`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      }
    );
  } catch(err) {
    throw new MessarioAiFetchFailure(
      'network',
      'Could not reach Messario Panel for training case.'
    );
  }

  const body = await readJsonBody(response);
  // 409 conflict still returns a case payload — surface it to the caller.
  if(!response.ok && response.status !== 409) {
    throw new MessarioAiFetchFailure(
      'rejected',
      (typeof body.message === 'string' && body.message) ||
        (typeof body.error === 'string' && body.error) ||
        'Messario rejected the training case request.',
      response.status
    );
  }

  return assertTrainingCaseResponse(body, response.status);
}

export async function createMessarioAiTrainingCase(
  token: string,
  panelOrigin: string,
  payload: {
    draft_generate_id: number,
    operator_feedback: string,
    expected_reply?: string,
    candidate_message?: string
  },
  fetchValue: typeof fetch = fetch
): Promise<MessarioAiTrainingCaseResponse> {
  return postMessarioAiTrainingCase(
    token,
    panelOrigin,
    '/api/telegram-web/ai/training-cases',
    payload,
    fetchValue
  );
}

export async function applyMessarioAiTrainingCase(
  token: string,
  panelOrigin: string,
  caseId: number,
  payload: {proposed_changes?: MessarioAiTrainingChange[]} = {},
  fetchValue: typeof fetch = fetch
): Promise<MessarioAiTrainingCaseResponse> {
  return postMessarioAiTrainingCase(
    token,
    panelOrigin,
    `/api/telegram-web/ai/training-cases/${caseId}/apply`,
    payload,
    fetchValue
  );
}

export async function rejectMessarioAiTrainingCase(
  token: string,
  panelOrigin: string,
  caseId: number,
  payload: {reason?: string} = {},
  fetchValue: typeof fetch = fetch
): Promise<MessarioAiTrainingCaseResponse> {
  return postMessarioAiTrainingCase(
    token,
    panelOrigin,
    `/api/telegram-web/ai/training-cases/${caseId}/reject`,
    payload,
    fetchValue
  );
}
